import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import SearchIcon from '@material-ui/icons/Search';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { RequirePermission } from '@backstage/plugin-permission-react';
import type { Permission } from '@backstage/plugin-permission-common';
import { manewcoTokens } from '@platform/plugin-platform-react';
import { SidebarLogo } from './SidebarLogo';
import { SidebarCollapsibleSection } from './SidebarCollapsibleSection';
import { ApplicationsSidebarItems } from './ApplicationsSidebarItems';
import { SidebarExpandableGroup } from './SidebarExpandableGroup';
import { SidebarUser } from './SidebarUser';
import { SignOutButton } from './SignOutButton';
import { ThemeToggle } from './ThemeToggle';
import { SidebarSearchBox } from './SidebarSearchBox';
import { SidebarGroupLink } from './SidebarGroupLink';
import {
  HIDDEN_PAGES,
  NAV_SECTIONS,
  allItems,
  isNavGroup,
  isPluginNavItem,
  type NavGroup,
  type NavSectionItem,
} from './navSections';

const useLineStyles = makeStyles(theme => ({
  root: { height: 1, width: '100%', background: manewcoTokens(theme).line, margin: '6px 0', flexShrink: 0 },
}));

const SidebarLine = () => <div className={useLineStyles().root} />;

/** Ne rend ses enfants qu'avec la permission — rien sinon (entrée masquée). */
const IfAllowed = ({ permission, children }: { permission?: Permission; children: ReactNode }) =>
  permission ? (
    <RequirePermission permission={permission as any} errorPage={<></>}>
      {children}
    </RequirePermission>
  ) : (
    <>{children}</>
  );

type NavData = { href: string; title: string; icon: JSX.Element };

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      // Entrées retirées du menu (pages toujours joignables par leur adresse).
      for (const id of HIDDEN_PAGES) navItems.take(id);

      // Toutes les entrées de plugin sont prélevées d'emblée : une section repliée
      // ne rend pas ses enfants, et une entrée non prélevée tomberait en bas du menu.
      const taken = new Map<string, NavData | undefined>();
      for (const section of NAV_SECTIONS) {
        for (const item of allItems(section)) {
          if (isPluginNavItem(item)) taken.set(item.take, navItems.take(item.take) as NavData | undefined);
        }
      }
      const rendered = (item: NavSectionItem | NavGroup): boolean =>
        isNavGroup(item) ? item.items.some(rendered) : !isPluginNavItem(item) || taken.get(item.take) !== undefined;
      const path = (item: NavSectionItem) => (isPluginNavItem(item) ? taken.get(item.take)?.href ?? '' : item.to);

      const entry = (item: NavSectionItem) => {
        if (!isPluginNavItem(item)) {
          return <SidebarItem key={item.to} icon={item.icon} to={item.to} text={item.text} />;
        }
        const data = taken.get(item.take)!;
        const Icon = item.icon;
        return (
          <IfAllowed key={item.take} permission={item.permission}>
            <SidebarItem
              icon={Icon ?? (() => data.icon)}
              to={data.href}
              text={item.text ?? data.title}
            />
          </IfAllowed>
        );
      };

      // Entrée d'un groupe : ligne compacte (voir SidebarGroupLink).
      const groupEntry = (item: NavSectionItem) => {
        if (!isPluginNavItem(item)) {
          return <SidebarGroupLink key={item.to} to={item.to} text={item.text} icon={item.icon} exact={item.exact} />;
        }
        const data = taken.get(item.take)!;
        return (
          <IfAllowed key={item.take} permission={item.permission}>
            <SidebarGroupLink
              to={data.href}
              text={item.text ?? data.title}
              icon={item.icon ?? (() => data.icon)}
              exact={item.exact}
            />
          </IfAllowed>
        );
      };

      // Entrées restantes (plugins non classés), rendues en bas du menu.
      const rest = navItems
        .withComponent(item => <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />)
        .rest({ sortBy: 'title' });

      return (
        <Sidebar disableExpandOnHover>
          <SidebarLogo />
          <SidebarGroup label="Rechercher" icon={<SearchIcon />} to="/search">
            <SidebarSearchBox />
          </SidebarGroup>
          <SidebarItem icon={HomeIcon} to="/" text="Vue d'ensemble" />
          <ApplicationsSidebarItems />
          {NAV_SECTIONS.filter(s => s.items.some(rendered)).map(section => (
            <SidebarCollapsibleSection
              key={section.id}
              label={section.label}
              defaultCollapsed={section.collapsedByDefault}
            >
              {section.items.filter(rendered).map(item =>
                isNavGroup(item) ? (
                  <SidebarExpandableGroup
                    key={item.group}
                    label={item.group}
                    icon={item.icon}
                    paths={item.items.filter(rendered).map(i => ({ path: path(i), exact: i.exact }))}
                  >
                    {item.items.filter(rendered).map(groupEntry)}
                  </SidebarExpandableGroup>
                ) : (
                  entry(item)
                ),
              )}
            </SidebarCollapsibleSection>
          ))}
          {rest.length > 0 && (
            <>
              <SidebarLine />
              {rest}
            </>
          )}
          <SidebarSpace />
          <SidebarLine />
          <NotificationsSidebarItem />
          <SidebarItem icon={SettingsIcon} to="/settings" text="Paramètres" />
          <ThemeToggle />
          <SidebarUser />
          <SignOutButton />
        </Sidebar>
      );
    },
  },
});
